import './App.css';
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider } from './AuthContext';
import AuthContext from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import Login from './components/Login';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import Register from './components/Register';
import Dashboard from './pages/Dashboard';

import TrainingTitle from './pages/Trainingtitle';
import BatchNo from './pages/Batchno';
import Organizationdetails from './pages/Organizationdetails';
import Companyinfo from './pages/Companyinfo';
import TrainersName from './pages/Trainersname';
import Worklocationdetails from './pages/Worklocation';
import CityName from './pages/Cityname';
import DrivingLicense from './pages/Drivinglicensedetails';
import CandidateRegistration from './pages/Candidateregistration';
import PostEvaluationTest from './pages/PostEvaluationTest';
import PreEvaluationTest from './pages/PreEvaluationTest';
import ParticipentFeedback from './pages/Participentfeedback';
import PersonalInteractionEvaluation from './pages/Personalinteractionevaluation';
import Certificateprint from './pages/Certificateprint';
import Renewal from './pages/Renewal';
import BatchReport from './pages/BatchReport';
import OverAllReport from './pages/OverAllReport';
import MonthlyReport from './pages/MonthlyReport';
import ConsolidatedReport from './pages/ConsolidatedReport';
import EvaluationForm from './pages/EvaluationForm';
import ViewEvaluationForm from './pages/ViewEvaluationForm';
import AttendanceSheet from './pages/AttendanceSheet';

import Profile from './pages/Profile';
import Changepassword from './pages/Changepassword';
import UserManagement from './pages/UserManagement';

const AppContent = () => {
  const { isAuthenticated, logout } = React.useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  // Check if the current path is "/login"
  const isLoginPage = location.pathname === '/login';

  // Auto-logout after 30 minutes of no user activity (mouse/keyboard/scroll/touch).
  // The timer resets on every activity event and only runs while logged in.
  useEffect(() => {
    if (!isAuthenticated) return;

    const IDLE_LIMIT_MS = 30 * 60 * 1000; // 30 minutes
    let idleTimer;

    const handleIdleLogout = () => {
      logout();
      navigate('/login');
      if (window.toastr) {
        window.toastr.info('You have been logged out due to inactivity.');
      }
    };

    const resetIdleTimer = () => {
      clearTimeout(idleTimer);
      idleTimer = setTimeout(handleIdleLogout, IDLE_LIMIT_MS);
    };

    const activityEvents = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    activityEvents.forEach((evt) => document.addEventListener(evt, resetIdleTimer));
    resetIdleTimer();

    return () => {
      clearTimeout(idleTimer);
      activityEvents.forEach((evt) => document.removeEventListener(evt, resetIdleTimer));
    };
  }, [isAuthenticated, logout, navigate]);

  // Native <select> dropdowns flip upward when there isn't enough room below
  // (e.g. a field near the end of a long modal form). Scrolling alone can't
  // help when the field is already at the end of the scrollable content, so
  // temporarily grow its scroll container's bottom padding to create real
  // room, then scroll the field toward that room before the browser decides
  // which way to open the popup. Removed again once the field loses focus.
  useEffect(() => {
    const MIN_SPACE_BELOW = 250;

    const findScrollableAncestor = (el) => {
      let node = el.parentElement;
      while (node && node !== document.body) {
        const style = getComputedStyle(node);
        if (/(auto|scroll)/.test(style.overflowY) && node.scrollHeight > node.clientHeight) {
          return node;
        }
        node = node.parentElement;
      }
      return document.scrollingElement || document.documentElement;
    };

    const handlePointerDown = (e) => {
      if (e.target.tagName !== 'SELECT') return;
      const select = e.target;
      const rect = select.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow >= MIN_SPACE_BELOW) return;

      const container = findScrollableAncestor(select);
      const originalPadding = container.style.paddingBottom;
      container.style.paddingBottom = `${MIN_SPACE_BELOW}px`;
      select.scrollIntoView({ block: 'center', behavior: 'auto' });

      const cleanup = () => {
        container.style.paddingBottom = originalPadding;
        select.removeEventListener('blur', cleanup);
      };
      select.addEventListener('blur', cleanup, { once: true });
    };

    document.addEventListener('mousedown', handlePointerDown, true);
    return () => document.removeEventListener('mousedown', handlePointerDown, true);
  }, []);

  // Define protected routes as an array of objects to map over
  const protectedRoutes = [
    { path: '/dashboard', element: <Dashboard /> },
    { path: '/profile', element: <Profile /> },
    { path: '/changepassword', element: <Changepassword /> },

    { path: '/training-title', element: <TrainingTitle /> },
    { path: '/batchno', element: <BatchNo /> },
    { path: '/organization-details', element: <Organizationdetails /> },
    { path: '/company-info', element: <Companyinfo /> },
    { path: '/trainers-name', element: <TrainersName /> },
    { path: '/work-location', element: <Worklocationdetails /> },
    { path: '/projectname', element: <CityName /> },
    { path: '/drivinglicense', element: <DrivingLicense /> },
    { path: '/candidate-registration', element: <CandidateRegistration /> },
    { path: '/pre-evaluation-test', element: <PreEvaluationTest /> },
    { path: '/post-evaluation-test', element: <PostEvaluationTest /> },
    { path: '/participent-feedback', element: <ParticipentFeedback /> },
    { path: '/personal-interaction-evaluation', element: <PersonalInteractionEvaluation /> },
    { path: '/certificate-print', element: <Certificateprint /> },
    { path: '/renewal', element: <Renewal /> },
    { path: '/batch-report', element: <BatchReport /> },
    { path: '/overall-report', element: <OverAllReport /> },
    { path: '/monthly-report', element: <MonthlyReport /> },
    { path: '/consolidated-report', element: <ConsolidatedReport /> },
    { path: '/evaluation-form', element: <EvaluationForm /> },
    { path: '/view-evaluation-form', element: <ViewEvaluationForm /> },
    { path: '/attendance-sheet', element: <AttendanceSheet /> },
    { path: '/user-management', element: <UserManagement />, adminOnly: true },
  ];


  return (
    <div className="App">
      {!isLoginPage ? (
        <div className="wrapper">
          {/* Only render sidebar, header, and footer if authenticated */}
          
          {isAuthenticated && <Header />}
          {isAuthenticated && <Sidebar />}
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route
              path="/"
              element={
                isAuthenticated ? <Navigate to="/dashboard" /> : <Navigate to="/login" />
              }
            />

            {/* Protected Routes */}
            {protectedRoutes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={<ProtectedRoute adminOnly={route.adminOnly}>{route.element}</ProtectedRoute>}
              />
            ))}
          </Routes>
          <aside className="control-sidebar control-sidebar-dark"></aside>
          {isAuthenticated && <Footer />}
        </div>
      ) : (
        // Render only the login page without the wrapper and main layout
        <Routes>
          <Route path="/login" element={<Login />} />
        </Routes>
      )}
    </div>
  );
}

const App = () => (
  <AuthProvider>
    <Router>
      <AppContent />
    </Router>
  </AuthProvider>
);

export default App;
